import { useEffect, useMemo, useState } from "react";
import { Mail, Eye, FileSignature, Download } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/auth";
import { WelcomeEmailModal } from "./WelcomeEmailModal";
import RecordActionsMenu from "./RecordActionsMenu";
import Pagination from "./Pagination";
import { welcomeButtonClass, welcomeButtonTitle, StatusDot } from "./shared";
import { devLog } from "@/lib/log";

const CoupleListPanel = ({ couples, onView, pagination }) => {
  const [q, setQ] = useState("");
  const [welcomeTarget, setWelcomeTarget] = useState(null);
  const [welcomeStatusMap, setWelcomeStatusMap] = useState({});
  const [notesSummary, setNotesSummary] = useState({});
  const [packSendingId, setPackSendingId] = useState(null);
  const [packStatusMap, setPackStatusMap] = useState({});
  const refreshWelcomeStatuses = async () => {
    try {
      const { data } = await api.get("/admin/welcome-email-status", {
        params: { role: "couple" },
      });
      setWelcomeStatusMap(data || {});
    } catch (err) {
      devLog("welcome-email-status (couple) fetch failed:", err?.message || err);
    }
  };
  const refreshNotesSummary = async () => {
    try {
      const { data } = await api.get("/admin/notes-summary", {
        params: { kind: "couple" },
      });
      setNotesSummary(data || {});
    } catch (err) {
      devLog("notes-summary (couple) fetch failed:", err?.message);
    }
  };
  const refreshPackStatuses = async () => {
    try {
      const { data } = await api.get("/admin/pack-email-status", {
        params: { role: "couple" },
      });
      setPackStatusMap(data || {});
    } catch (err) {
      devLog("pack-email-status (couple) fetch failed:", err?.message);
    }
  };
  useEffect(() => {
    refreshWelcomeStatuses();
    refreshNotesSummary();
    refreshPackStatuses();
  }, []);
  const sendCouplePack = async (cpl) => {
    if (!cpl?.id || packSendingId) return;
    if (!cpl.email) {
      toast("This couple has no email on file", { duration: 2400 });
      return;
    }
    setPackSendingId(cpl.id);
    try {
      const { data } = await api.post(
        `/admin/couple-signups/${cpl.id}/send-pack`
      );
      toast(
        data?.sent
          ? `Onboarding pack (${data.documents_attached.length} docs) sent to ${data.recipient}`
          : `Logged — email service unavailable`,
        { duration: 3400 }
      );
      refreshNotesSummary();
      refreshPackStatuses();
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't send the pack", {
        duration: 2800,
      });
    } finally {
      setPackSendingId(null);
    }
  };
  const approved = useMemo(
    () => (couples || []).filter((c) => c.status === "approved"),
    [couples]
  );
  const term = q.trim().toLowerCase();
  const sorted = useMemo(() => {
    const list = !term
      ? approved
      : approved.filter((c) =>
          (c.couple_full_name || "").toLowerCase().includes(term)
        );
    // Sort by wedding date ascending; rows without a date go to the end
    return [...list].sort((a, b) => {
      const ad = a.wedding_date || "";
      const bd = b.wedding_date || "";
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return ad.localeCompare(bd);
    });
  }, [approved, term]);

  return (
    <div data-testid="section-couple-list">
      <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
        Roster
      </span>
      <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
        Couple List
      </h2>
      <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-xl">
        All approved couples, ordered by wedding date. Search by couple name.
      </p>

      <div className="mt-6">
        <input
          data-testid="couple-list-search"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by couple name…"
          className="w-full max-w-sm h-11 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
        />
      </div>

      <div className="mt-6 border border-[var(--ld-border)]/80 overflow-x-auto">
        <table className="w-full text-sm" data-testid="couple-list-table">
          <thead className="bg-[var(--ld-ink)]/[0.03] text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
            <tr>
              <th className="text-left font-accent font-normal px-4 py-3">
                Couple
              </th>
              <th className="text-left font-accent font-normal px-4 py-3">
                Wedding date
              </th>
              <th className="text-left font-accent font-normal px-4 py-3">
                Consultation notes
              </th>
              <th className="text-right font-accent font-normal px-4 py-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--ld-border)]/70">
            {sorted.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-10 text-center text-[var(--ld-ink-muted)] italic"
                >
                  {approved.length === 0
                    ? "No approved couples yet."
                    : "No matches."}
                </td>
              </tr>
            )}
            {sorted.map((c) => {
              const dateStr = c.wedding_date
                ? new Date(c.wedding_date + "T00:00:00").toLocaleDateString()
                : "—";
              const notes = c.consultation_notes;
              return (
                <tr
                  key={c.id}
                  data-testid={`couple-list-row-${c.id}`}
                  className="hover:bg-[var(--ld-ink)]/[0.02]"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--ld-ink)]">
                      {c.couple_full_name || "—"}
                    </div>
                    <div className="text-xs text-[var(--ld-ink-muted)]">
                      {c.email}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--ld-ink)] whitespace-nowrap">
                    {dateStr}
                  </td>
                  <td className="px-4 py-3 text-[var(--ld-ink)] text-[13px] leading-[1.6] max-w-md whitespace-pre-wrap">
                    {(() => {
                      const summary = notesSummary[c.id];
                      const latest = summary?.latest || notes;
                      if (!latest) {
                        return (
                          <span className="italic text-[var(--ld-ink-muted)]">
                            No notes yet
                          </span>
                        );
                      }
                      return (
                        <span data-testid={`couple-list-note-${c.id}`}>
                          {latest}
                          {summary?.count > 1 && (
                            <span className="text-[var(--ld-ink-muted)]"> · +{summary.count - 1} more</span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        data-testid={`couple-list-welcome-${c.id}`}
                        data-welcome-status={welcomeStatusMap[c.id]?.status || "none"}
                        onClick={() => setWelcomeTarget({ role: "couple", id: c.id })}
                        title={welcomeButtonTitle(
                          welcomeStatusMap[c.id]?.status,
                          welcomeStatusMap[c.id]?.sent_at,
                          c.email
                        )}
                        className={`inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border px-3 py-1.5 transition-colors ${welcomeButtonClass(welcomeStatusMap[c.id]?.status)}`}
                      >
                        <Mail size={13} strokeWidth={1.6} />
                        Welcome
                      </button>
                      <a
                        data-testid={`couple-list-download-${c.id}`}
                        href={`${API}/admin/couple-signups/${c.id}/download`}
                        download
                        title="Download completed form (PDF)"
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)] px-3 py-1.5 hover:bg-[var(--ld-gold-deep)] hover:text-white transition-colors"
                      >
                        <Download size={13} strokeWidth={1.7} />
                        Download
                      </a>
                      <button
                        data-testid={`couple-list-send-pack-${c.id}`}
                        onClick={() => sendCouplePack(c)}
                        disabled={packSendingId === c.id}
                        title={`Email the full onboarding pack (policies) to ${c.email || "this couple"}`}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/40 text-[var(--ld-ink)] bg-[var(--ld-gold-soft)]/30 px-3 py-1.5 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] hover:border-[var(--ld-ink)] transition-colors disabled:opacity-60 disabled:cursor-wait"
                      >
                        <FileSignature size={13} strokeWidth={1.6} />
                        {packSendingId === c.id ? "Sending…" : "Send pack"}
                        <StatusDot
                          status={packStatusMap[c.id]?.status}
                          testid={`couple-list-pack-status-${c.id}`}
                        />
                      </button>
                      <button
                        data-testid={`couple-list-view-${c.id}`}
                        onClick={() => onView(c)}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
                      >
                        <Eye size={13} strokeWidth={1.6} />
                        Open
                      </button>
                      <RecordActionsMenu
                        kind="couple"
                        targetId={c.id}
                        email={c.email}
                        title={c.couple_full_name || c.email || "Couple"}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pagination && (
        <Pagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onPageChange={pagination.onPageChange}
          loading={pagination.loading}
        />
      )}
      <WelcomeEmailModal
        target={welcomeTarget}
        onClose={() => setWelcomeTarget(null)}
        onSent={() => {
          refreshWelcomeStatuses();
          refreshNotesSummary();
        }}
      />
    </div>
  );
};

export default CoupleListPanel;
